import { useEffect, useRef, useState, useCallback } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

/**
 * Custom hook that manages a STOMP-over-WebSocket connection to Spring Boot.
 *
 * @param {string} url          - SockJS endpoint, e.g. "http://localhost:8080/ws"
 * @param {string[]} topics     - STOMP topics to subscribe to, e.g. ["/topic/messages"]
 * @param {function} onMessage  - Callback invoked with (topic, parsedBody) on each message
 */
export function useWebSocket(url, topics, onMessage) {
  const clientRef = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const client = new Client({
      // Use SockJS as the transport factory
      webSocketFactory: () => new SockJS(url),
      reconnectDelay: 5000,

      onConnect: () => {
        setConnected(true);
        topics.forEach((topic) => {
          client.subscribe(topic, (frame) => {
            try {
              const body = JSON.parse(frame.body);
              onMessage(topic, body);
            } catch {
              onMessage(topic, frame.body);
            }
          });
        });
      },

      onDisconnect:     () => setConnected(false),
      onWebSocketClose: () => setConnected(false), // unexpected drop
      onWebSocketError: () => setConnected(false), // network/transport error

      onStompError: (frame) => {
        console.error('STOMP error', frame);
        setConnected(false);
      },
    });

    client.activate();
    clientRef.current = client;

    return () => {
      client.deactivate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  /** Send a message to a Spring @MessageMapping destination. */
  const send = useCallback((destination, body) => {
    if (clientRef.current?.connected) {
      clientRef.current.publish({
        destination,
        body: JSON.stringify(body),
      });
    } else {
      console.warn('WebSocket not connected – message not sent');
    }
  }, []);

  return { connected, send };
}
